import { levelAt, type AccessUser } from "@shared/access/check";
import { directoryTree, type AccessDirectory } from "@shared/access/directory";
import type { Grant, PersonalLevelSetting } from "@shared/access/grants";
import type { HomeKind } from "@shared/access/homeKind";
import {
  isPersonalLevel,
  type Level,
  type PersonalLevel,
} from "@shared/access/levels";
import { isBelow, samePlace, type Place } from "@shared/access/places";
import {
  areaOf,
  isResourcePath,
  RESOURCE_PATHS,
  type ResourcePath,
} from "@shared/access/resources";
import { isRoleName, ROLES, type RoleName } from "@shared/access/roles";
import type { WireGrant } from "@shared/access/userAccess";
import { translateKey, type Translate } from "../../i18n/translate";

/** One role and set of personal levels, given on several places at once. */
export interface GrantLine {
  readonly role: RoleName | null;
  readonly personalLevels: readonly PersonalLevelSetting[];
  readonly places: readonly Place[];
}

/** The Users page form: the usual grant, and the places that differ from it. */
export interface GrantForm {
  readonly main: GrantLine;
  readonly exceptions: readonly Grant[];
}

export const EMPTY_FORM: GrantForm = {
  main: { role: null, personalLevels: [], places: [] },
  exceptions: [],
};

export function fromWire(g: WireGrant): Grant {
  return {
    place: g.place,
    role: g.role && isRoleName(g.role) ? g.role : null,
    personalLevels: g.personalLevels.flatMap((p) =>
      isResourcePath(p.resource) && isPersonalLevel(p.level)
        ? [{ resource: p.resource, level: p.level }]
        : []
    ),
  };
}

function signature(g: {
  role: RoleName | null;
  personalLevels: readonly PersonalLevelSetting[];
}): string {
  return JSON.stringify([
    g.role,
    [...g.personalLevels].map((p) => `${p.resource}=${p.level}`).sort(),
  ]);
}

/**
 * Groups stored grants into the form. The usual grant is the group holding
 * the home place, or else the largest group; every other grant is an exception.
 */
export function formFromGrants(
  wire: readonly WireGrant[],
  home: Place | null
): GrantForm {
  const grants = wire.map(fromWire);
  if (grants.length === 0) return EMPTY_FORM;
  const groups = new Map<string, Grant[]>();
  for (const g of grants) {
    const key = signature(g);
    groups.set(key, [...(groups.get(key) ?? []), g]);
  }
  const ordered = [...groups.values()].sort((a, b) => b.length - a.length);
  const main =
    (home &&
      ordered.find((list) => list.some((g) => samePlace(g.place, home)))) ??
    ordered[0]!;
  return {
    main: {
      role: main[0]!.role,
      personalLevels: main[0]!.personalLevels,
      places: main.map((g) => g.place),
    },
    exceptions: grants.filter((g) => !main.includes(g)),
  };
}

/** The grants the form stands for. An exception replaces the usual grant on its place. */
export function grantsFromForm(form: GrantForm): Grant[] {
  const usual = form.main.places
    .filter((p) => !form.exceptions.some((e) => samePlace(e.place, p)))
    .map((place) => ({
      place,
      role: form.main.role,
      personalLevels: form.main.personalLevels,
    }));
  return [...usual, ...form.exceptions];
}

export function toWire(grants: readonly Grant[]): WireGrant[] {
  return grants.map((g) => ({
    place: { kind: g.place.kind as "company" | "premise", id: g.place.id },
    role: g.role,
    personalLevels: g.personalLevels.map((p) => ({
      resource: p.resource,
      level: p.level,
    })),
  }));
}

export function resourceLabel(t: Translate, path: ResourcePath): string {
  return translateKey(t, `users:grantForm.resources.${path}`);
}

export function levelLabel(t: Translate, level: PersonalLevel | Level): string {
  return translateKey(t, `users:grantForm.levels.${level}`);
}

/** A place by its name; a place missing from the directory gets no name rather than its id. */
export function placeLabel(
  t: Translate,
  directory: AccessDirectory,
  place: Place
): string {
  if (place.kind === "company") {
    if (place.id === directory.nendaCompanyId) return t("common:labels.nenda");
    return (
      directory.companies.find((c) => c.id === place.id)?.name ??
      t("users:grantForm.unknownCompany")
    );
  }
  return (
    directory.premises.find((p) => p.id === place.id)?.name ??
    t("users:grantForm.unknownPremise")
  );
}

export function roleLabel(t: Translate, role: RoleName | null): string {
  return role
    ? translateKey(t, `users:grantForm.roles.${role}`)
    : t("users:grantForm.noRole");
}

/** "A, B and C" in the current language; one item is returned as is. */
export function joinWords(t: Translate, words: readonly string[]): string {
  return t("users:grantForm.list", { items: words });
}

function placesPhrase(
  t: Translate,
  places: readonly Place[],
  directory: AccessDirectory
): string {
  const parts: string[] = [];
  const companies = places.filter((p) => p.kind === "company");
  const premises = places.filter((p) => p.kind !== "company");
  for (const c of companies) {
    parts.push(
      c.id === directory.nendaCompanyId
        ? t("users:grantForm.everywhere")
        : t("users:grantForm.atAllPremisesOf", {
            company: placeLabel(t, directory, c),
          })
    );
  }
  if (premises.length > 0) {
    parts.push(
      t("users:grantForm.onPlaces", {
        places: premises.map((p) => placeLabel(t, directory, p)),
      })
    );
  }
  return joinWords(t, parts);
}

function levelsPhrase(
  t: Translate,
  levels: readonly PersonalLevelSetting[]
): string {
  return levels
    .map((p) =>
      t("users:grantForm.levelOn", {
        resource: resourceLabel(t, p.resource).toLowerCase(),
        level: levelLabel(t, p.level),
      })
    )
    .join(", ");
}

function whatPhrase(
  t: Translate,
  g: {
    role: RoleName | null;
    personalLevels: readonly PersonalLevelSetting[];
  }
): string {
  const levels = levelsPhrase(t, g.personalLevels);
  if (g.role && levels)
    return t("users:grantForm.roleWithLevels", {
      role: roleLabel(t, g.role),
      levels,
    });
  if (g.role) return roleLabel(t, g.role);
  return levels || t("users:grantForm.nothingOfItsOwn");
}

/**
 * The form in plain words, for example "Manager at all Chain X premises,
 * except Hotel C (Viewer)".
 */
export function describeForm(
  t: Translate,
  form: GrantForm,
  directory: AccessDirectory
): string {
  const tree = directoryTree(directory);
  const { main } = form;
  const sentences: string[] = [];
  const exceptions = new Map<string, Grant[]>();
  for (const e of form.exceptions) {
    const key = signature(e);
    exceptions.set(key, [...(exceptions.get(key) ?? []), e]);
  }
  const inside: string[] = [];
  const beside: string[] = [];
  for (const group of exceptions.values()) {
    const places = group.map((g) => g.place);
    const within = places.every((p) =>
      main.places.some((m) => samePlace(m, p) || isBelow(p, m, tree))
    );
    const what = whatPhrase(t, group[0]!);
    if (within) {
      inside.push(
        t("users:grantForm.placesWith", {
          places: places.map((p) => placeLabel(t, directory, p)),
          what,
        })
      );
    } else {
      beside.push(
        t("users:grantForm.whatWhere", {
          what,
          where: placesPhrase(t, places, directory),
        })
      );
    }
  }
  if (main.places.length > 0) {
    const head = t("users:grantForm.whatWhere", {
      what: whatPhrase(t, main),
      where: placesPhrase(t, main.places, directory),
    });
    sentences.push(
      inside.length > 0
        ? t("users:grantForm.headExcept", { head, exceptions: inside })
        : head
    );
  }
  sentences.push(...beside);
  if (sentences.length === 0) return t("users:grantForm.noAccess");
  const text = sentences.join("; ");
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
}

export type LevelSource =
  | { readonly kind: "personal" }
  | { readonly kind: "role" }
  | { readonly kind: "area"; readonly area: ResourcePath }
  | { readonly kind: "none" }
  | { readonly kind: "above" };

export interface ShownLevel {
  /** null when the grant leaves the resource to the place above. */
  readonly level: PersonalLevel | null;
  readonly source: LevelSource;
}

function ownSetting(
  role: RoleName | null,
  levels: readonly PersonalLevelSetting[],
  path: ResourcePath
): ShownLevel | null {
  const personal = levels.find((p) => p.resource === path);
  if (personal) return { level: personal.level, source: { kind: "personal" } };
  const fromRole = role
    ? (ROLES[role].levels as Partial<Record<ResourcePath, PersonalLevel>>)[path]
    : undefined;
  if (fromRole) return { level: fromRole, source: { kind: "role" } };
  return null;
}

/**
 * The level one grant gives a resource, and where it comes from: "view, from
 * Signage" for a resource that takes its area's level.
 */
export function shownLevel(
  role: RoleName | null,
  levels: readonly PersonalLevelSetting[],
  path: ResourcePath
): ShownLevel {
  const own = ownSetting(role, levels, path);
  if (own) return own;
  const area = areaOf(path);
  if (area) {
    const fromArea = ownSetting(role, levels, area);
    if (fromArea)
      return { level: fromArea.level, source: { kind: "area", area } };
  }
  return role
    ? { level: "none", source: { kind: "none" } }
    : { level: null, source: { kind: "above" } };
}

export function describeShownLevel(t: Translate, shown: ShownLevel): string {
  const level = shown.level ? levelLabel(t, shown.level) : "";
  switch (shown.source.kind) {
    case "personal":
      return t("users:grantForm.shownLevel.setHere", { level });
    case "role":
      return t("users:grantForm.shownLevel.fromRole", { level });
    case "area":
      return t("users:grantForm.shownLevel.fromArea", {
        level,
        area: resourceLabel(t, shown.source.area),
      });
    case "none":
      return t("users:grantForm.shownLevel.none");
    case "above":
      return t("users:grantForm.shownLevel.above");
  }
}

export const AREAS: readonly ResourcePath[] = RESOURCE_PATHS.filter(
  (p) => areaOf(p) === null
);

export function childrenOf(area: ResourcePath): ResourcePath[] {
  return RESOURCE_PATHS.filter((p) => areaOf(p) === area);
}

/** Sets or clears (`null`) one personal level. */
export function withPersonalLevel(
  levels: readonly PersonalLevelSetting[],
  resource: ResourcePath,
  level: PersonalLevel | null
): PersonalLevelSetting[] {
  const rest = levels.filter((p) => p.resource !== resource);
  return level === null ? rest : [...rest, { resource, level }];
}

export interface ResultRow {
  readonly resource: ResourcePath;
  readonly levels: readonly PersonalLevel[];
}

/** The user's level on every resource at each place, after the holder limit. */
export function resultByPlace(
  grants: readonly Grant[],
  kind: HomeKind,
  places: readonly Place[],
  directory: AccessDirectory
): ResultRow[] {
  const tree = directoryTree(directory);
  const user: AccessUser = { kind, grants };
  return RESOURCE_PATHS.map((resource) => ({
    resource,
    levels: places.map((place) => levelAt(user, resource, place, tree)),
  }));
}
